import type { RouteObject } from 'react-router';

import { Suspense } from 'react';
import { Outlet } from 'react-router';

import lazyWithReload from 'src/utils/lazyWithReload';

import { CONFIG } from 'src/global-config';
import { DashboardLayout } from 'src/layouts/dashboard';
import { PermissionWrapper } from 'src/pages/auth/hooks/permissionWrapper';

import { LoadingScreen } from 'src/components/loading-screen';

import { AuthGuard } from 'src/auth/guard';

import { usePathname } from '../../../routes/hooks';

// Lazy-loaded pages
const OrganizationPage = lazyWithReload(() => import('src/pages/setting/components/organization'));
const UsersListPage = lazyWithReload(() => import('src/pages/setting/components/users/users-list'));
const TeamsListPage = lazyWithReload(() => import('src/pages/setting/components/teams/teams-list'));
const CustomLabelListPage = lazyWithReload(
  () => import('src/pages/setting/components/custom-labels')
);
const TemplateListPage = lazyWithReload(() => import('src/pages/setting/components/templates'));
const WhatsappTemplate = lazyWithReload(
  () => import('src/pages/setting/components/whatsapp-templates')
);
const WhatsappTemplateAdd = lazyWithReload(
  () => import('src/pages/setting/components/whatsapp-templates/whatsapp-template-add')
);
const WhatsappTemplateCreateFlow = lazyWithReload(
  () => import('src/pages/setting/components/whatsapp-templates/whatsapp-template-create-flow')
);
const DidManagePage = lazyWithReload(() => import('src/pages/setting/components/did-manage'));
const SnippetsPage = lazyWithReload(() => import('src/pages/setting/components/snippets'));
const UserDetailsPage = lazyWithReload(() => import('src/pages/setting/user-details'));
const TeamDetailsPage = lazyWithReload(
  () => import('src/pages/setting/components/teams/teamDetailPage')
);
const BillingPage = lazyWithReload(() => import('src/pages/setting/components/billing'));
const BillingDetail = lazyWithReload(
  () => import('src/pages/setting/components/billing/invoice-detail')
);
const WebhookPage = lazyWithReload(
  () => import('src/pages/setting/components/webhooks/webhook-list')
);
const CustomFieldsPage = lazyWithReload(() => import('src/pages/setting/components/custom-fields'));
const DiscountPage = lazyWithReload(() => import('src/pages/setting/components/discounts'));
const LifeCyclePage = lazyWithReload(() => import('src/pages/setting/lifecycle'));
const LineTemplate = lazyWithReload(
  () => import('src/pages/setting/components/line-templates/LineTemplatePage')
);
const LineTemplateAdd = lazyWithReload(
  () => import('src/pages/setting/components/line-templates/lineTemplateAdd')
);
const LineTemplateEdit = lazyWithReload(
  () => import('src/pages/setting/components/line-templates/lineTemplateEdit')
);
const AuditLogPage = lazyWithReload(
  () => import('src/pages/setting/components/audit-log/audit-log-list')
);
const AuditLogDetailsPage = lazyWithReload(
  () => import('src/pages/setting/components/audit-log/audit-log-details')
);
const ComingSoonPage = lazyWithReload(() => import('src/pages/error/coming-soon'));
const AISettingsPage = lazyWithReload(() => import('src/pages/setting/components/ai-settings'));
const AiUsagePage = lazyWithReload(() => import('src/pages/setting/components/ai-usage'));
const AutoAssignRoutingPage = lazyWithReload(
  () => import('src/pages/setting/components/auto-assign-routing')
);
const ChannelFeatureFlagsPage = lazyWithReload(
  () => import('src/pages/setting/components/channel-feature-flags/index')
);
const ChatbotListPage = lazyWithReload(() =>
  import('src/pages/setting/components/chatbots').then((m) => ({ default: m.ChatbotListView }))
);
const ChatbotFormPage = lazyWithReload(() =>
  import('src/pages/setting/components/chatbots').then((m) => ({ default: m.ChatbotFormView }))
);
const VoicebotListPage = lazyWithReload(() =>
  import('src/pages/setting/components/voicebots').then((m) => ({ default: m.VoicebotListView }))
);
const VoicebotFormPage = lazyWithReload(() =>
  import('src/pages/setting/components/voicebots').then((m) => ({ default: m.VoicebotFormView }))
);
const PermissionsPage = lazyWithReload(() => import('src/pages/permissions/permissions-page'));
const CalendarSettingPage = lazyWithReload(() => import('src/pages/setting/components/calendar'));
const SLAPoliciesPage = lazyWithReload(() => import('src/pages/setting/components/sla-policies'));
const TaskStatusSettingsPage = lazyWithReload(
  () => import('src/pages/setting/components/task-statuses/task-status-settings')
);
const CalendarSuccessPage = lazyWithReload(
  () => import('src/pages/setting/components/calendar/components/calendar-success')
);
// const TemplateCreatePage = lazyWithReload(
//   () => import('src/pages/setting/components/whatsapptemplate/templateCreate')
// );

// Suspense wrapper with dynamic key for pathname
function SuspenseOutlet() {
  const pathname = usePathname();
  return (
    <Suspense key={pathname} fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  );
}

// Layout wrapped in auth guard if needed
const getLayout = () =>
  CONFIG.auth.skip ? (
    <DashboardLayout>
      <SuspenseOutlet />
    </DashboardLayout>
  ) : (
    <AuthGuard>
      <DashboardLayout>
        <SuspenseOutlet />
      </DashboardLayout>
    </AuthGuard>
  );

// Helper to wrap a component with PermissionWrapper
const withPermission = (
  module: string | string[],
  Component: React.LazyExoticComponent<React.ComponentType<any>>,
  isAdminOnly?: boolean
) => (
  <PermissionWrapper module={module} isAdminOnly={isAdminOnly}>
    <Component />
  </PermissionWrapper>
);

// Route config
export const settingRoutes: RouteObject[] = [
  {
    path: 'setting',
    element: getLayout(),
    children: [
      {
        index: true,
        element: withPermission('user', UsersListPage),
      },
      {
        path: 'organization',
        element: withPermission('organization', OrganizationPage),
      },
      {
        path: 'user',
        element: null,
        children: [
          {
            index: true,
            element: withPermission('user', UsersListPage),
          },
          {
            path: ':id/details',
            element: withPermission('user', UserDetailsPage),
          },
        ],
      },
      {
        path: 'teams',
        element: null,
        children: [
          {
            index: true,
            element: withPermission('team', TeamsListPage),
          },
          {
            path: ':id/details',
            element: withPermission('team', TeamDetailsPage),
          },
        ],
      },

      {
        path: 'custom-labels',
        element: withPermission('label', CustomLabelListPage),
      },
      // {
      //   path: 'template',
      //   element: withPermission('template', TemplateListPage),
      // },
      {
        path: 'whatsapp-template',
        element: null,
        children: [
          {
            index: true,
            element: withPermission('template', WhatsappTemplate),
          },
          {
            path: 'add',
            element: withPermission('template', WhatsappTemplateAdd),
          },
          {
            path: 'add/flow',
            element: withPermission('template', WhatsappTemplateCreateFlow),
          },
        ],
      },
      {
        path: 'did-manage',
        element: withPermission('call', DidManagePage),
      },
      {
        path: 'billing',
        element: null,
        children: [
          {
            index: true,
            element: withPermission(['billing', 'bill'], BillingPage),
          },
          {
            path: ':id/invoice',
            element: withPermission(['billing', 'bill'], BillingDetail),
          },
        ],
      },
      {
        path: 'webhook',
        element: withPermission('webhook', WebhookPage),
      },
      {
        path: 'custom-fields',
        element: withPermission('custom-fields', CustomFieldsPage),
      },
      {
        path: 'discount',
        element: withPermission('discount', DiscountPage),
      },
      {
        path: 'snippets',
        element: withPermission('snippet', SnippetsPage),
      },
      {
        path: 'lifecycle',
        element: withPermission('lifecycle', LifeCyclePage),
      },
      {
        path: 'permissions',
        element: withPermission('roles', PermissionsPage),
      },
      {
        path: 'calendar',
        children: [
          { index: true, element: withPermission('calendar', CalendarSettingPage) },
          { path: 'success', element: withPermission('calendar', CalendarSuccessPage) },
        ],
      },
      {
        path: 'linetemplate',
        element: null,
        children: [
          { index: true, element: withPermission('linetemplate', LineTemplate) },
          { path: 'add', element: withPermission('linetemplate', LineTemplateAdd) },
          { path: ':id/edit', element: withPermission('linetemplate', LineTemplateEdit) },
        ],
      },
      {
        path: 'audit-log',
        element: null,
        children: [
          {
            index: true,
            element: withPermission('audit_log', AuditLogPage),
          },
          {
            path: 'details/:auditId',
            element: withPermission('audit_log', AuditLogDetailsPage),
          },
        ],
      },
      {
        path: 'ai-settings',
        element: withPermission('ai_settings', AISettingsPage),
      },
      {
        path: 'ai-usage',
        element: withPermission('ai_usage', AiUsagePage),
      },
      {
        path: 'auto-assign-routing',
        element: withPermission('auto_assign', AutoAssignRoutingPage),
      },
      {
        path: 'sla-policy',
        element: withPermission('sla_policy', SLAPoliciesPage),
      },
      {
        path: 'task-statuses',
        element: withPermission('task', TaskStatusSettingsPage),
      },
      // {
      //   path: 'channel-feature-flags',
      //   element: withPermission('channel', ChannelFeatureFlagsPage),
      // },
      {
        path: 'chatbots',
        element: null,
        children: [
          { index: true, element: withPermission('chatbot', ChatbotListPage) },
          { path: 'new', element: withPermission('chatbot', ChatbotFormPage) },
          { path: ':id/edit', element: withPermission('chatbot', ChatbotFormPage) },
        ],
      },
      {
        path: 'voicebots',
        element: null,
        children: [
          { index: true, element: withPermission('voicebot', VoicebotListPage) },
          { path: 'new', element: withPermission('voicebot', VoicebotFormPage) },
          { path: ':id/edit', element: withPermission('voicebot', VoicebotFormPage) },
        ],
      },
    ],
  },
];
